import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { validate as uuidValidate } from 'uuid';
import { PolicyModel } from '../models/api/governance/policy.model';

export const DomainValidatorName = 'domain';
export const NoSpaceValidatorName = 'noSpace';
export const guidValidatorName = 'guid';
export const certificateThumbprintValidatorName = 'certificateThumbprint';
export const certificatePrivateKeyValidatorName = 'certificatePrivateKey';
export const emailValidatorName = 'email';
export const urlValidatorName = 'url';
export const textValidationValidatorName = 'textValidation';
export const salesforceInstanceUrlValidatorName = 'salesforceInstanceUrl';
export const serviceNowInstanceUrlValidatorName = 'serviceNowInstanceUrl';
export const chatGptEnterpriseApiKeyValidatorName = 'chatGptEnterpriseApiKey';
export const amazonBedrockIamRoleArnValidatorName = 'amazonBedrockIamRoleArn';
export const accountIdMismatchValidatorName = 'accountIdMismatch';

const secureUrlRegex = /^(https:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;
const urlRegex = /^(http(s)?:\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/;
const urlRegexWithPathParams = /^(http(s)?:\/\/.)?[-a-zA-Z0-9._]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9_./]*)[*]?$/;
const chatGptApiKeyPrefix = 'sk-proj-';

export class AppValidators {
  public static domain(control: AbstractControl): ValidationErrors | null {
    const DOMAIN: string = control.value;
    const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z0-9][a-z0-9-]{0,61}[a-zA-Z0-9]$/;
    let result = true;

    if (!DOMAIN || !DOMAIN_REGEX.test(DOMAIN)) {
      result = false;
    }

    return result ? null : { [DomainValidatorName]: true };
  }

  public static noSpace(control: AbstractControl): ValidationErrors | null {
    if ((control.value as string).indexOf(' ') >= 0) {
      return { [NoSpaceValidatorName]: true };
    }

    return null;
  }

  public static noDollarSign(control: AbstractControl): ValidationErrors | null {
    if ((control.value as string).indexOf('$') >= 0) {
      return { ['noDollarSign']: true };
    }

    return null;
  }

  public static certificateThumbprint(control: AbstractControl): ValidationErrors | null {
    if (control.value && !/^[0-9a-fA-F]{40}$/.test(control.value as string)) {
      return { [certificateThumbprintValidatorName]: true };
    }

    return null;
  }

  public static certificatePrivateKey(control: AbstractControl): ValidationErrors | null {
    if (
      control.value &&
      !/^-----(\bBEGIN\b).*(PRIVATE KEY\b)-----\n(.|\n)*\n-----(\bEND\b).*(PRIVATE KEY\b)-----\n?$/.test(
        control.value as string
      )
    ) {
      return { [certificatePrivateKeyValidatorName]: true };
    }

    return null;
  }

  public static guid(control: AbstractControl): ValidationErrors | null {
    if (control.value && !uuidValidate(control.value as string)) {
      return { [guidValidatorName]: true };
    }

    return null;
  }

  public static email(control: AbstractControl): ValidationErrors | null {
    if (control.value && !AppValidators._emailIsValid(control.value as string)) {
      return { [emailValidatorName]: true };
    }

    return null;
  }

  public static url(control: AbstractControl): ValidationErrors | null {
    if (control.value && !AppValidators._validateRegex(urlRegex, control.value as string)) {
      return { [urlValidatorName]: true };
    }

    return null;
  }

  public static urlWithPathParams(control: AbstractControl): ValidationErrors | null {
    if (control.value && !AppValidators._validateRegex(urlRegexWithPathParams, control.value as string)) {
      return { [urlValidatorName]: true };
    }

    return null;
  }

  public static secureUrl(control: AbstractControl): ValidationErrors | null {
    if (control.value && !AppValidators._validateRegex(secureUrlRegex, control.value as string)) {
      return { [urlValidatorName]: true };
    }

    return null;
  }

  public static salesforceInstanceUrl(control: AbstractControl): ValidationErrors | null {
    const SALESFORCE_INSTANCE_URL_REGEXP = /^https:\/\/[a-zA-Z0-9_\-\.]+\.salesforce\.com$/;

    if (control.value && !SALESFORCE_INSTANCE_URL_REGEXP.test(control.value as string)) {
      return { [salesforceInstanceUrlValidatorName]: true };
    }

    return null;
  }

  public static serviceNowInstanceUrl(control: AbstractControl): ValidationErrors | null {
    const SERVICENOW_INSTANCE_URL_REGEXP = /^https:\/\/[a-zA-Z0-9_\-\.]+\.service-now\.com$/;

    if (control.value && !SERVICENOW_INSTANCE_URL_REGEXP.test(control.value as string)) {
      return { [serviceNowInstanceUrlValidatorName]: true };
    }

    return null;
  }

  public static chatGptEnterpriseApiKey(control: AbstractControl): ValidationErrors | null {
    if (control.value && !control.value.startsWith(chatGptApiKeyPrefix)) {
      return { [chatGptEnterpriseApiKeyValidatorName]: true };
    }

    return null;
  }

  public static amazonBedrockIamRoleArn(control: AbstractControl): ValidationErrors | null {
    const AWS_IAM_ROLE_ARN_REGEXP = /^arn:aws:iam::\d{12}:role\/[\w+=,.@-]+$/;

    if (control.value && !AWS_IAM_ROLE_ARN_REGEXP.test(control.value as string)) {
      return { [amazonBedrockIamRoleArnValidatorName]: true };
    }

    return null;
  }

  public static multipleEmailsBySeparators(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const splittedEmails = (control.value as string).split(/[,;]+/);

      if (splittedEmails.length > max) {
        return { [emailValidatorName]: true };
      }

      for (const emailIndex in splittedEmails) {
        const emailClean = splittedEmails[emailIndex].trim();
        if (emailClean != '') {
          if (!AppValidators._emailIsValid(emailClean)) {
            return { [emailValidatorName]: true };
          }
        }
      }
      return null;
    };
  }

  public static textValidation(text: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value !== text) {
        return { [textValidationValidatorName]: true };
      }
      return null;
    };
  }

  public static evenNumberOfQuoteMarks(control: AbstractControl): ValidationErrors | null {
    const numOfQuotes = ((control.value as string)?.match(/"/g) ?? []).length;
    if (numOfQuotes % 2 !== 0) {
      return { ['oddNumberOfQuoteMarks']: true };
    }
    return null;
  }

  public static uniqueEndpointFromPolicy(policy: PolicyModel): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const endpointsToAdd = control.value as { url: string }[];
      const urlsToAdd = endpointsToAdd.map((endpoint) => endpoint.url);
      const existingUrls = policy.config.endpoints.map((endpoint) => endpoint.url);
      // merge 2 lists of endpoints
      const urls = existingUrls.concat(urlsToAdd);
      const uniqueUrls = [...new Set(urls)];
      if (urls.length !== uniqueUrls.length) {
        return { ['uniqueEndpointFromPolicy']: true };
      }
      return null;
    };
  }

  public static maxHostnames(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const endpoints = control.value as { url: string }[];
      if (endpoints.length > max) {
        return { ['maxHostnames']: true };
      }
      return null;
    };
  }

  private static _validateRegex(regex: RegExp, text: string): boolean {
    return regex.test(text.toLowerCase());
  }

  private static _emailIsValid(email: string): boolean {
    // This regex was taken from https://emailregex.com/ which provides a very accurate RFCs based email regex.
    // This regex is used to verify the email in our Python backend as well, if you wish to change it first look for other places that use it and make sure to align them as well with the change.
    // Going forward when we have a mechanism to sync configurations between client side code and server side code we'll define the regex in one place used by both.
    const EMAIL_REGEXP =
      /^(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/;

    return EMAIL_REGEXP.test(email.toLowerCase());
  }
}
